import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, Users, MapPin } from 'lucide-react';
import { useEvents } from '../contexts/EventsContext';
import { formatDateBR, formatTimeBR, formatDateTimeBR } from '../utils/dateUtils';

const CalendarPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [saoPauloTime, setSaoPauloTime] = useState(new Date());
  const { events, loading, getEventsForDate } = useEvents();

  // Atualizar horário de São Paulo a cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      // Converter para horário de São Paulo (UTC-3)
      const saoPauloOffset = -3 * 60; // -3 horas em minutos
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const saoPauloDateTime = new Date(utc + (saoPauloOffset * 60000));
      setSaoPauloTime(saoPauloDateTime);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Eventos agora vêm do contexto (sem eventos hardcoded)

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Juli', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Dias do mês anterior
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Completar com dias do próximo mês
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({ date: nextDate, isCurrentMonth: false });
    }
    
    return days;
  };

  // getEventsForDate agora vem do contexto
  
  const getEventLabelStyle = (labelColor: string) => {
    // Determinar se o texto deve ser claro ou escuro baseado na cor de fundo
    const isLightColor = labelColor === '#0FFF55' || labelColor.toLowerCase().includes('fff') || labelColor.toLowerCase().includes('yellow');
    return {
      backgroundColor: labelColor,
      color: isLightColor ? '#121316' : '#ffffff'
    };
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const days = getDaysInMonth(currentDate);
  const today = new Date();

  return (
    <div className="flex-1 bg-[#121316] min-h-screen pt-20">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-[#1a1d21] via-[#121316] to-[#0f1114] border-b border-gray-800 md:-ml-[70px] xl:-ml-[80px] w-full md:w-[calc(100%+70px)] xl:w-[calc(100%+80px)]">
        <div className="relative p-8 md:pl-[102px] xl:pl-[112px]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  <span className="text-[#0FFF55]">Calendário</span> de Eventos
                </h1>
                <p className="text-gray-300 text-lg">
                  Acompanhe calls, workshops e eventos importantes da plataforma
                </p>
              </div>
              <div className="text-right">
                <div className="text-white font-bold text-2xl">
                  {saoPauloTime.toLocaleTimeString('pt-BR', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </div>
                <div className="text-gray-400 text-sm">
                  São Paulo - {saoPauloTime.toLocaleDateString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="xl:col-span-2">
            <div className="bg-[#1a1d21] rounded-3xl border border-gray-700 p-6">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={prevMonth}
                    className="p-2 text-gray-400 hover:text-white hover:bg-[#2a2d31] rounded-lg transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextMonth}
                    className="p-2 text-gray-400 hover:text-white hover:bg-[#2a2d31] rounded-lg transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              {/* Week Days */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div key={day} className="p-2 text-center text-sm font-semibold text-gray-400">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => {
                  const dayEvents = getEventsForDate(day.date);
                  const isToday = day.date.toDateString() === today.toDateString();
                  const hasEvents = dayEvents.length > 0;
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[100px] p-2 border border-gray-700 rounded-lg transition-colors hover:bg-[#2a2d31] relative ${
                        !day.isCurrentMonth ? 'opacity-30' : ''
                      } ${isToday ? 'bg-[#0FFF55]/10 border-[#0FFF55]' : ''}`}
                    >
                      <div className={`text-sm font-semibold mb-1 flex items-center justify-between ${
                        isToday ? 'text-[#0FFF55]' : day.isCurrentMonth ? 'text-white' : 'text-gray-500'
                      }`}>
                        <span>{day.date.getDate()}</span>
                        {/* Bolinha verde para dias com eventos */}
                        {hasEvents && day.isCurrentMonth && (
                          <div className="w-2 h-2 bg-[#0FFF55] rounded-full"></div>
                        )}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 2).map(event => {
                          return (
                            <div
                              key={event.id}
                              className="text-xs px-2 py-1 rounded-md flex items-center space-x-1"
                              style={getEventLabelStyle(event.label_color)}
                            >
                              <Clock size={10} />
                              <span className="truncate">
                                {formatTimeBR(event.event_date)}
                              </span>
                            </div>
                          );
                        })}
                        {dayEvents.length > 2 && (
                          <div className="text-xs text-gray-400 px-2">
                            +{dayEvents.length - 2} mais
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Events List */}
          <div className="space-y-6">
            <div className="bg-[#1a1d21] rounded-3xl border border-gray-700 p-6">
              <h3 className="text-xl font-bold text-white mb-4">Próximos Eventos</h3>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-6 h-6 border-4 border-[#0FFF55] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-400">Carregando eventos...</p>
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400">Nenhum evento agendado</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {events.slice(0, 5).map(event => {
                    return (
                      <div
                        key={event.id}
                        className="bg-[#121316] rounded-2xl p-4 border border-gray-600 hover:border-[#0FFF55] transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-white font-semibold text-sm leading-tight">
                            {event.title}
                          </h4>
                          <div 
                            className="px-2 py-1 rounded-full text-xs font-semibold"
                            style={getEventLabelStyle(event.label_color)}
                          >
                            {event.custom_label}
                          </div>
                        </div>
                        
                        <div className="text-gray-400 text-xs space-y-1">
                          <div className="flex items-center space-x-2">
                            <Clock size={12} />
                            <span>
                              {formatDateTimeBR(event.event_date)}
                            </span>
                          </div>
                          {event.participants_limit && (
                            <div className="flex items-center space-x-2">
                              <Users size={12} />
                              <span>Até {event.participants_limit} participantes</span>
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center space-x-2">
                              <MapPin size={12} />
                              <span>{event.location}</span>
                            </div>
                          )}
                        </div>
                        
                        {event.description && (
                          <p className="text-gray-300 text-xs mt-2 leading-relaxed">
                            {event.description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {events.length > 5 && (
                    <div className="text-center">
                      <p className="text-gray-400 text-sm">
                        +{events.length - 5} eventos adicionais
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;